<template>
  <div class="tree-node">
    <div
      :class="nodeClass"
      :style="nodeStyle"
      role="treeitem"
      :aria-expanded="hasChildren ? isExpanded : undefined"
      :aria-level="level + 1"
      :aria-selected="isSelected"
      :tabindex="isSelected ? 0 : -1"
      @click="onNodeClick"
      @keydown="onKeyDown"
  @contextmenu.prevent.stop="onContextMenu"
    >
      <!-- Unified SVG (ancestor verticals + current trunk + elbow) -->
      <svg
        v-if="showCurrentVerticalLine"
        class="tree-lines"
        :width="lineConfig.width"
        :height="lineConfig.height"
        :viewBox="`0 0 ${lineConfig.width} ${lineConfig.height}`"
        fill="none"
        shape-rendering="crispEdges"
      >
        <template v-for="x in lineConfig.ancestorXs" :key="`anc-${node.id}-${x}`">
          <path :d="`M${x} 0 L${x} ${lineConfig.height}`" :stroke="lineColor" stroke-width="1" />
        </template>
        <!-- Current vertical trunk: special case for first root => omit segment above elbow -->
        <path
          v-if="currentVerticalPath"
          :d="currentVerticalPath"
          :stroke="lineColor"
          stroke-width="1"
        />
        <path :d="`M${lineConfig.currentX} ${lineConfig.mid} L${lineConfig.currentX + lineConfig.elbowLength} ${lineConfig.mid}`" :stroke="lineColor" stroke-width="1" />
      </svg>
      
      <!-- Indentation space (grid column 1) -->
      <div class="tree-indent-space"></div>
      
      <!-- Expand/Collapse Toggle (grid column 2) -->
      <button
        v-if="(isContainer || hasChildren) && !node.data?.isAddRoot && !node.data?.isAddChild"
        class="tree-toggle"
        :aria-label="isExpanded ? 'Collapse' : 'Expand'"
        @click.stop="onToggleClick"
      >
        <Icon
          name="caret-down"
          :size="12"
          :class="['tree-caret', { 'tree-caret--collapsed': !isExpanded }]"
        />
      </button>
      <div v-else class="tree-toggle tree-toggle--placeholder" :class="{ 'tree-toggle--hidden': node.data?.isAddRoot || node.data?.isAddChild }"></div>

      <!-- Node Label (grid column 3) -->
      <span class="tree-label" :class="{ 'tree-label--add-root': node.data?.isAddRoot, 'tree-label--add-child': node.data?.isAddChild }">
        <template v-if="node.data?.isAddRoot">
          <button
            type="button"
            class="tree-add-btn"
            @click.stop="emitAddRoot"
            aria-label="Add root property"
          >
            <Icon name="plus" :size="12" />
          </button>
        </template>
        <template v-else-if="node.data?.isAddChild">
          <button
            type="button"
            class="tree-add-btn"
            @click.stop="emitAddChild"
            :aria-label="`Add child to ${node.data?.parentType || 'object'}`"
          >
            <Icon name="plus" :size="12" />
          </button>
        </template>
        <template v-else>
          <!-- <LockIcon v-if="node.data?.readonly && !canEdit" size="small" class="tree-label-lock" /> -->
          {{ node.label }}
        </template>
      </span>
      
      <!-- Value editor (grid column 4) -->
      <component
        v-if="!isContainer && !hasChildren && node.data && !node.data.isGroup && !node.data?.isAddRoot && !node.data?.isAddChild"
        :is="getValueEditorComponent(node.data.type)"
        :key="`${props.presetId || 'default'}-${node.id}-${node.data.type}`"
        :model-value="getDisplayValue(node.data.value, node.data.type)"
        :type="getInputType(node.data.type)"
        :readonly="node.data.readonly || node.data.type === 'array'"
        class="tree-value-editor"
        @update:model-value="onValueUpdate"
        @click.stop
      />
      <div v-else class="tree-value-editor tree-value-editor--placeholder"></div>
    </div>

  <!-- Debug logs removed -->

    <!-- Children -->
    <div v-if="isExpanded && (hasChildren || showAddChildSentinel)" class="tree-children" role="group">
      <TreeNode
        v-for="(child, index) in virtualChildren"
        :key="`${props.presetId || 'default'}-${child.id}`"
        :node="child"
        :level="level + 1"
        :selected-id="selectedId"
        :expanded-ids="expandedIds"
        :show-icons="showIcons"
        :show-lines="showLines"
        :indent-size="indentSize"
        :ancestor-lines="getAncestorLinesForChild(index, virtualChildren.length)"
        :is-last-child="index === virtualChildren.length - 1"
        :active-node-id="activeNodeId"
        :preset-id="presetId"
        :can-edit="props.canEdit"
        :parent-shows-add-child-sentinel="showAddChildSentinel"
        @select="$emit('select', $event)"
        @expand="$emit('expand', $event)"
        @collapse="$emit('collapse', $event)"
        @value-update="(nodeId, value) => $emit('valueUpdate', nodeId, value)"
  @add-child="$emit('addChild', $event)"
  @context-menu="$emit('context-menu', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Icon, InputBox, Toggle } from '@/components/atoms';
// import LockIcon from '@/components/atoms/LockIcon.vue';
import type { TreeViewNode } from './TreeView.vue';

export interface TreeNodeProps {
  node: TreeViewNode;
  level: number;
  selectedId?: string;
  expandedIds?: Set<string>;
  showIcons?: boolean;
  showLines?: boolean;
  indentSize?: number;
  ancestorLines?: boolean[]; // Track which ancestor levels need vertical lines
  isLastChild?: boolean; // Whether this is the last child in its parent's children array
  activeNodeId?: string;
  presetId?: string;
  isFirstRoot?: boolean;
  canEdit?: boolean; // inherit from TreeView to know if add sentinel should display
  parentShowsAddChildSentinel?: boolean; // whether this node's parent rendered an add-child sentinel (edit mode & parent can accept children)
}

const props = defineProps<TreeNodeProps>();

// NOTE: Use kebab-case for emitted events so parent listeners match exactly (@context-menu)
const emit = defineEmits<{
  'select': [nodeId: string];
  'expand': [nodeId: string];
  'collapse': [nodeId: string];
  'valueUpdate': [nodeId: string, value: any];
  'addChild': [payload: { nodeId: string; isArray: boolean }];
  'context-menu': [payload: { nodeId: string; x: number; y: number }];
}>();

const hasChildren = computed(() => {
  if(!props.node.children) return false;
  return props.node.children.filter(c=>!c.data?.isAddChild && !c.data?.isAddRoot).length > 0;
});

// Debug: log node basics once
if (import.meta.env.DEV) {
  try {
    console.debug('[TreeNode:init]', {
      id: props.node.id,
      label: props.node.label,
      type: props.node.data?.type,
      level: props.level,
      readonly: props.node.data?.readonly,
      hasChildrenInitial: hasChildren.value,
      isAddRoot: props.node.data?.isAddRoot,
      isAddChild: props.node.data?.isAddChild
    });
  } catch {}
}

const showAddChildSentinel = computed(()=> !!props.canEdit && canAddChild.value);
const filteredChildren = computed(()=> (props.node.children||[]).filter(c=>!c.data?.isAddChild));
const virtualChildren = computed(()=> {
  if(!showAddChildSentinel.value) return filteredChildren.value;
  return [
    ...filteredChildren.value,
    { id: props.node.id + '__add', label:'', icon:'plus', data:{ isAddChild:true, parentId: props.node.id, parentType: props.node.data?.type, section: props.node.data?.section } }
  ];
});

const isContainer = computed(()=> (props.node.data?.type === 'object' || props.node.data?.type === 'array') && !props.node.data?.isAddChild && !props.node.data?.isAddRoot);
// Allow adding children to any non-readonly container (object/array) regardless of section
const canAddChild = computed(()=> isContainer.value && !props.node.data?.readonly);
if (import.meta.env.DEV) {
  try { console.debug('[TreeNode:canAddChild]', props.node.id, '=>', canAddChild.value, { isContainer: isContainer.value }); } catch {}
}

const isSelected = computed(() => 
  props.selectedId === props.node.id
);

const isExpanded = computed(() => 
  props.expandedIds?.has(props.node.id) ?? false
);

// Need line to extend downward if there are visible children (expanded) or sentinel, even when last sibling
// Determine if a vertical trunk continues beyond this node.
// Rules:
// 1. If this node is *not* the last visible sibling -> continue.
// 2. If it IS the last visible sibling but the parent would show an add-child sentinel (edit mode)
//    then we also continue so the line connects visually to that sentinel.
// 3. The synthetic add-child / add-root sentinel itself never continues downward.
const hasDownConnection = computed(() => {
  // Sentinels never extend lines downward
  if (props.node.data?.isAddChild || props.node.data?.isAddRoot) return false;
  if (!props.isLastChild) return true;
  if (props.parentShowsAddChildSentinel) return true; // edit mode: implicit sibling (add sentinel)
  return false;
});

// Terminal node (no visible continuation below in this mode)
// isTerminal no longer needed with unified pseudo-element approach
// Always show lines for every node (vertical truncated via CSS when terminal)
const showCurrentVerticalLine = computed(() => true);
const lineColor = 'var(--vscode-tree-indentGuidesStroke, rgba(128,128,128,0.55))';
const lineConfig = computed(() => {
  const indent = props.indentSize || 16;
  const offset = 15;
  const height = 32;
  const mid = height / 2;
  const elbowLength = 7;
  const ancestorXs = (props.ancestorLines || [])
    .map((show, idx) => show ? offset + idx * indent : null)
    .filter((v): v is number => v !== null);
  const currentX = offset + props.level * indent;
  const width = currentX + elbowLength + 2; // a little extra to prevent clipping
  return { ancestorXs, currentX, width, height, mid, elbowLength };
});

// For first root node we want an upside-down L: omit the vertical segment above the elbow.
// Rules:
// - If first root & hasDownConnection -> draw from mid down to bottom.
// - If first root & no down connection (single root, no sentinel) -> no vertical segment at all (only elbow will render).
// - Otherwise (non-first-root) preserve existing behavior: full height if continues, else from top to mid.
const currentVerticalPath = computed(() => {
  const { currentX, height, mid } = lineConfig.value;
  if (props.isFirstRoot) {
    if (hasDownConnection.value) {
      return `M${currentX} ${mid} L${currentX} ${height}`;
    }
    // No downward continuation: omit vertical entirely
    return '';
  }
  // Non-first-root logic (legacy): if continuing, full height else top->mid
  return hasDownConnection.value
    ? `M${currentX} 0 L${currentX} ${height}`
    : `M${currentX} 0 L${currentX} ${mid}`;
});

const nodeClass = computed(() => [
  'tree-node-item',
  {
    'tree-node-item--selected': isSelected.value,
    'tree-node-item--disabled': props.node.disabled,
    'tree-node-item--expanded': isExpanded.value,
    'tree-node-item--has-children': hasChildren.value,
  }
]);

const nodeStyle = computed(() => {
  const indentSize = props.indentSize || 16;
  
  return {
    '--level': props.level,
    '--indent-size': `${indentSize}px`,
    '--base-indent': `${props.level * indentSize}px`,
  };
});

// Compute ancestor lines for children
const getAncestorLinesForChild = (_childIndex: number, _totalChildren: number): boolean[] => {
  const currentAncestorLines = (props.ancestorLines || []).map(Boolean);
  // Append whether THIS node's trunk continues (i.e., this node is NOT last among its siblings)
  return [...currentAncestorLines, hasDownConnection.value];
};

// Helper function to get appropriate editor component for property type
const getValueEditorComponent = (type: string) => {
  switch (type) {
    case 'boolean': return Toggle
    case 'string': return InputBox
    case 'number':
    case 'integer': return InputBox
    case 'array': return InputBox
    default: return InputBox
  }
}

// Helper function to map schema types to HTML input types
const getInputType = (type: string): 'text' | 'number' | 'password' | 'email' | 'url' => {
  switch (type) {
    case 'number':
    case 'integer': return 'number'
    case 'email': return 'email'
    case 'url': return 'url'
    default: return 'text'
  }
}

// Helper function to format display values for different types
const getDisplayValue = (value: any, type: string) => {
  if (type === 'array') {
    return JSON.stringify(value)
  }
  return value
}

// Handle value updates from inline editors
const onValueUpdate = (value: any) => {
  emit('valueUpdate', props.node.id, value)
}

const onNodeClick = () => {
  if (props.node.data?.isAddRoot) {
    emitAddRoot();
    return;
  }
  if (props.node.data?.isAddChild) {
    emitAddChild();
    return;
  }
  if (!props.node.disabled) {
    emit('select', props.node.id);
    if (hasChildren.value || isContainer.value) {
      if (isExpanded.value) {
        emit('collapse', props.node.id);
      } else {
        emit('expand', props.node.id);
      }
    }
  }
};

const onToggleClick = () => {
  if (isContainer.value || hasChildren.value) {
    if (isExpanded.value) {
      emit('collapse', props.node.id);
    } else {
      emit('expand', props.node.id);
    }
  }
};

const onKeyDown = (event: KeyboardEvent) => {
  // Don't intercept keyboard events when user is editing in an input/textarea
  const target = event.target as HTMLElement;
  const isEditingInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
  
  switch (event.key) {
    case 'Enter':
    case ' ':
      if (!isEditingInput) {
        event.preventDefault();
        onNodeClick();
      }
      break;
    case 'ContextMenu':
    case 'F10':
      if (event.shiftKey || event.key === 'ContextMenu') {
        event.preventDefault();
        openContextMenuViaKeyboard(event);
      }
      break;
    case 'Insert':
      if (props.node.data?.isAddChild) {
        event.preventDefault();
        emitAddChild();
      } else if (canAddChild.value) {
        event.preventDefault();
        emitAddChild();
      }
      break;
    case 'ArrowRight':
      if (!isEditingInput) {
        event.preventDefault();
        if (hasChildren.value && !isExpanded.value) {
          emit('expand', props.node.id);
        }
      }
      break;
    case 'ArrowLeft':
      if (!isEditingInput) {
        event.preventDefault();
        if (hasChildren.value && isExpanded.value) {
          emit('collapse', props.node.id);
        }
      }
      break;
  }
};

function onContextMenu(e: MouseEvent){
  if (import.meta.env.DEV) {
    // try { console.debug('[TreeNode:onContextMenu]', props.node.id, { x: e.clientX, y: e.clientY }); } catch {}
  }
  
  // Special handling for add sentinels - always show context menu
  const isAddSentinel = props.node.data?.isAddChild || props.node.data?.isAddRoot;
  
  // Only show context menu in edit mode OR for collapsible items (object/array) OR for add sentinels
  const shouldShowContextMenu = props.canEdit || isContainer.value || isAddSentinel;
  
  if (!shouldShowContextMenu) {
    return; // Suppress context menu for read-only leaf nodes
  }

  if (!isAddSentinel) {
    emit('select', props.node.id); // focus/select regular nodes before opening the context menu
  }
  emit('context-menu', { nodeId: props.node.id, x: e.clientX, y: e.clientY });
}

function openContextMenuViaKeyboard(e: KeyboardEvent){
  // Special handling for add sentinels - always show context menu
  const isAddSentinel = props.node.data?.isAddChild || props.node.data?.isAddRoot;
  
  // Only show context menu in edit mode OR for collapsible items (object/array) OR for add sentinels
  const shouldShowContextMenu = props.canEdit || isContainer.value || isAddSentinel;
  
  if (!shouldShowContextMenu) {
    return; // Suppress context menu for read-only leaf nodes
  }
  
  const target = e.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  const x = rect.left + 24; // offset a bit right of toggle
  const y = rect.top + rect.height / 2;
  if (import.meta.env.DEV) {
    try { console.debug('[TreeNode:openContextMenuViaKeyboard]', props.node.id, { x, y }); } catch {}
  }
  emit('context-menu', { nodeId: props.node.id, x, y });
}

function emitAddChild(){
  // If this is the synthetic add-child sentinel, target its parent instead
  const targetId = props.node.data?.isAddChild ? props.node.data.parentId : props.node.id;
  const isArray = props.node.data?.isAddChild ? props.node.data.parentType === 'array' : props.node.data?.type === 'array';
  emit('addChild', { nodeId: targetId, isArray });
}

function emitAddRoot(){
  // Re-use select path so TreeView can intercept and emit addRoot upward
  emit('select', props.node.id);
}
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.tree-node {
  --level: 0;
  --indent-size: 16px;
  container-type: inline-size;
}

.tree-node-item {
  display: grid;
  grid-template-columns: 
    [indent-start] var(--base-indent) 
    [indicator-start] min-content 
    [label-start] minmax(100px, 1fr) 
    [input-start] minmax(350px, 75cqw) [input-end];
  grid-template-areas: "indent indicator label input";
  align-items: center;
  gap: 8px 16px;
  padding: 4px 0;
  cursor: pointer;
  border-radius: var(--border-radius-sm);
  transition: background-color 0.1s ease;
  outline: none;
  height: 32px; /* force even height for consistent 50% center pixel */
  min-height: 32px;
  position: relative;
  
  &:hover:not(.tree-node-item--disabled) {
    background-color: var(--vscode-list-hoverBackground, transparent);
    outline: 1px solid var(--vscode-list-focusOutline, var(--vscode-focusBorder));
    outline-offset: -1px;
  }
  
  &--selected {
    background-color: var(--vscode-list-hoverBackground, transparent);
    outline: 1px solid var(--vscode-focusBorder);
    outline-offset: -1px;
  }
  
  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:focus {
    @include focus-outline;
  }
}

/* Unified SVG lines */
.tree-lines {
  position: absolute;
  top: 0;
  left: 0;
  height: 32px;
  pointer-events: none;
  z-index: 4;
  overflow: visible;
}

// Remove the old left/right sections - no longer needed
.tree-node-left,
.tree-node-right {
  display: none;
}

.tree-indent-space {
  // This empty div creates the indentation space in the grid
  grid-column: 1;
}

.tree-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  color: var(--vscode-foreground);
  opacity: 0.7;
  transition: opacity 0.1s ease, background-color 0.1s ease;
  grid-area: indicator;
  position: relative;
  left: 6px;
  
  &:hover {
    opacity: 1;
    background-color: var(--vscode-list-hoverBackground);
  }
  
  &:focus {
    @include focus-outline;
  }
  
  &--placeholder {
    cursor: default;
    opacity: 0;
    width: 18px;
    position: relative;
    left: 6px;
  }
  &--hidden {
    display:none;
  }
}

/* Caret rotation */
.tree-caret {
  transition: transform 0.12s ease;
}
.tree-caret--collapsed {
  transform: rotate(-90deg);
}

.tree-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: inherit;
  grid-area: label;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  .tree-node-item--disabled & {
    opacity: 0.5;
  }
}

.tree-label-lock {
  flex-shrink: 0;
  margin-right: 2px;
}

/* For sentinel add nodes, allow outline to escape label box */
.tree-label--add-root,
.tree-label--add-child {
  overflow: visible;
  text-overflow: unset;
}

/* Root add button now uses same .tree-add-btn style as child add buttons */

.tree-add-btn {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:18px;
  height:18px;
  border:none;
  background:transparent;
  padding:0;
  border-radius:3px;
  cursor:pointer;
  color: var(--vscode-foreground);
  opacity:.6;
  transition: background-color .1s ease, opacity .1s ease;
  &:hover { opacity:1; background: var(--vscode-list-hoverBackground); }
  &:focus, &:focus-visible { @include focus-outline; }
}

/* Align add sentinel buttons horizontally with expand/collapse toggle */
.tree-label--add-root .tree-add-btn,
.tree-label--add-child .tree-add-btn {
  /* Pull left so it visually sits where the toggle would be */
  margin-left:-6px;
}

.tree-value-editor {
  grid-area: input;
  width: 100%;
  display: flex;
  align-items: center;
  
  &--placeholder {
    visibility: hidden;
  }
  
  // Override default input styling to be more compact and aligned
  :deep(input) {
    padding: 2px 6px;
    font-size: 11px;
    height: 22px;
    border: 1px solid transparent;
    background: var(--vscode-input-background);
    color: var(--vscode-input-foreground);
    border-radius: 2px;
    transition: border-color 0.1s ease;
    width: 100%;
    box-sizing: border-box;
    
    &:hover {
      border-color: var(--vscode-input-border);
    }
    
    &:focus {
      border-color: var(--vscode-focusBorder);
      outline: none;
      background: var(--vscode-editor-background);
    }
  }
  
  // Style for toggle components - align with inputs
  :deep(.toggle) {
    margin: 0;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    
    // Don't override the checkbox input styling - let Toggle component handle it
    .toggle-input {
      margin: 0;
    }
  }
  
  // Style for select/dropdown components - match input height
  :deep(select) {
    padding: 2px 6px;
    font-size: 11px;
    height: 22px;
    border: 1px solid transparent;
    background: var(--vscode-input-background);
    color: var(--vscode-input-foreground);
    border-radius: 2px;
    width: 100%;
    box-sizing: border-box;
    
    &:hover {
      border-color: var(--vscode-input-border);
    }
    
    &:focus {
      border-color: var(--vscode-focusBorder);
      outline: none;
    }
  }
}

.tree-children {
  margin-left: 0;
}

.tree-add-child-row {
  display: flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 8px 2px 0;
  .tree-add-child-indent { flex: 0 0 auto; }
  .tree-add-btn { margin-left: 4px; }
}

// Focus management for keyboard navigation
.tree-node-item {
  &[tabindex="0"] {
    position: relative;
  }
}
</style>
